(ns practitest.frontend.components2.dashboard.charts.axis
  (:require ["d3" :as d3]
            [clojure.string :as str]
            [practitest.frontend.components2.dashboard.charts.common :as c]
            [practitest.frontend.common.format-utils :as format]
            [tick.core :as t]))

(defn- rotate-ticks! [sel {:keys [label-angle]}]
  (when label-angle
    (-> (.selectAll sel ".tick text")
        (.attr "transform" (str "rotate(" label-angle ")"))
        (.attr "dx" (if (neg? label-angle) "-0.5em" "0.5em"))
        (.attr "dy" "0.5em")
        (.style "text-anchor" (if (neg? label-angle) "end" "start")))))

(defn- text-length!
  [^js text-el s]
  (set! (.-textContent text-el) s)
  (.getComputedTextLength text-el))

(defn- ellipsize-label!
  [^js text-el full max-w]
  (let [overflow? (> (text-length! text-el full) max-w)]
    (when overflow?
      (let [n (loop [lo 0 hi (dec (count full))]
                (if (< lo hi)
                  (let [mid (quot (+ lo hi 1) 2)]
                    (if (<= (text-length! text-el (str (subs full 0 mid) "…")) max-w)
                      (recur mid hi)
                      (recur lo (dec mid))))
                  lo))]
        (text-length! text-el (str (subs full 0 n) "…"))))
    overflow?))

(defn- rotated-max-label-width
  [angle-deg bottom-padding]
  (/ bottom-padding (js/Math.sin (* (js/Math.abs angle-deg) (/ js/Math.PI 180)))))

(defn- discrete-scale?
  [d3-scale]
  (boolean (and d3-scale (fn? (.-step d3-scale)))))

(defn- max-label-width
  [{:keys [axis type d3-scale]} bottom-padding]
  (when (and (not= :temporal type) (discrete-scale? d3-scale))
    (if-let [angle (:label-angle axis)]
      (when bottom-padding (rotated-max-label-width angle bottom-padding))
      (.step d3-scale))))

(defn- tick-anchor-x
  [d3-scale d]
  (let [x (d3-scale d)]
    (if (fn? (.-bandwidth d3-scale))
      (+ x (/ (.bandwidth d3-scale) 2))
      x)))

(defn- wire-label-tooltip!
  "Shows the chart's tooltip with the label's full text on hover, anchored
   above the label, pointing down at it."
  [^js text-el full {:keys [d3-scale d height tooltip]}]
  (let [{:keys [show! hide!]} tooltip]
    (-> (.select d3 text-el)
        (.on "mouseenter" (fn [] (show! {:category  full
                                         :rows      []
                                         :placement :above
                                         :anchor-x  (tick-anchor-x d3-scale d)
                                         :anchor-y  height})))
        (.on "mouseleave" (fn [] (hide!))))))

(defn- truncate-labels!
  [sel {:keys [d3-scale] :as channel} {:keys [bottom-padding height tooltip]}]
  (when-let [max-w (max-label-width channel bottom-padding)]
    (-> sel
        (.selectAll ".tick text")
        (.each (fn [^js d]
                 (this-as this
                   (let [full (.-textContent this)]
                     (when (and (ellipsize-label! this full max-w) tooltip)
                       (wire-label-tooltip!
                        this full {:d3-scale d3-scale :d d :height height :tooltip tooltip})))))))))

(defn- show-every-nth-label! [sel n]
  (-> (.selectAll sel ".tick text")
      (.style "display" (fn [_ i]
                          (if (zero? (mod i n)) "block" "none")))))

(defn- d3-format [type fmt]
  (case type
    :temporal     (.utcFormat d3 fmt)
    :quantitative (.format d3 fmt)
    nil))

(defn- exact-tick-values [scale n]
  (let [[d0 d1] (.domain scale)
        t0      (.valueOf d0)
        t1      (.valueOf d1)
        step    (/ (- t1 t0) (dec n))]
    (clj->js (mapv #(+ t0 (* % step)) (range n)))))

(defn- cross-len [orient width height]
  (case orient
    (:top :bottom) height
    (:left :right) width))

(defn- orient->d3-axis [orient d3-scale]
  (case orient
    :top    (.axisTop d3 d3-scale)
    :bottom (.axisBottom d3 d3-scale)
    :left   (.axisLeft d3 d3-scale)
    :right  (.axisRight d3 d3-scale)))

(defn- min-step-tick-values [scale tick-min-step]
  (let [[d0 d1] (.domain scale)
        start   (* tick-min-step (js/Math.ceil (/ d0 tick-min-step)))
        end     (* tick-min-step (js/Math.floor (/ d1 tick-min-step)))
        n-steps (if (<= start end) (/ (- end start) tick-min-step) -1)]
    (if (< n-steps 8)
      (clj->js (range start (+ end tick-min-step) tick-min-step))
      (->> (.ticks scale 8)
           js->clj
           (map #(-> (/ % tick-min-step)
                     js/Math.round
                     (* tick-min-step)))
           distinct
           sort
           clj->js))))

(defn tick-step
  [scale tick-min-step]
  (let [values (js->clj (if (and tick-min-step (pos? tick-min-step))
                          (min-step-tick-values scale tick-min-step)
                          (.ticks scale)))
        [a b]  (take-last 2 values)]
    (when (and a b) (- b a))))

(defn- axis-format [type format d3-scale valid-step?]
  (or format
      (when (= type :quantitative)
        (let [[d0 d1]   (.domain d3-scale)
              max-value (max (js/Math.abs d0)
                              (js/Math.abs d1))]
          (cond
            (>= max-value 1000) ".3~s"
            valid-step?        ".0f")))))

(defn- d3-axis [{:keys [type d3-scale axis]} width height]
  (let [{:keys [orient format tick-count values ticks grid tick-min-step]} axis
        tick-size      (- (cross-len orient width height))
        q?             (= type :quantitative)
        valid-step?    (and (number? tick-min-step)
                            (pos? tick-min-step))
        fmt            (axis-format type format d3-scale valid-step?)]
    (cond-> (orient->d3-axis orient d3-scale)
      fmt                            (.tickFormat (d3-format type fmt))
      (and q? valid-step?
           (not (false? ticks)))     (.tickValues (min-step-tick-values d3-scale tick-min-step))
      values                    (.tickValues (clj->js values))
      (and (not valid-step?) (not values) tick-count)
                                     (.tickValues (exact-tick-values d3-scale tick-count))
      grid                           (-> (.tickSize tick-size) (.tickSizeOuter 0))
      (false? ticks)                 (.tickValues #js []))))

(defn- title-transform [orient width height]
  (case orient
    :top    (str "translate(" (/ width 2) ",-25)")
    :bottom (str "translate(" (/ width 2) ",35)")
    :left   (str "translate(-40," (/ height 2) ") rotate(-90)")
    :right  (str "translate(40," (/ height 2) ") rotate(90)")))

(defn- ensure-title! [sel {:keys [orient title]} width height]
  (when title
    (-> (c/ensure-el! sel "text" "axis-title")
        (.attr "text-anchor" "middle")
        (.attr "transform" (title-transform orient width height))
        (.text title))))

(defn- segment-domain!
  "Breaks the axis's single continuous baseline path into one segment per
   band, leaving a gap over each band's padding."
  [sel d3-scale]
  (let [bw (.bandwidth d3-scale)
        d  (->> (js->clj (.domain d3-scale))
                (map (fn [cat]
                       (let [x0 (d3-scale cat)]
                         (str "M" x0 ",0L" (+ x0 bw) ",0"))))
                (str/join ""))]
    (-> sel (.select "path.domain") (.attr "d" d))))

(defn- render-axis!
  [sel {:keys [axis scale d3-scale] :as channel} {:keys [width height bottom-padding tooltip]}]
  (when channel
    (let [d3-ax     (d3-axis channel width height)
          sel       (-> (.call sel d3-ax)
                        (.classed "with-grid" (boolean (:grid axis))))
          label-stride (:label-stride axis)]
      (when label-stride
        (show-every-nth-label! sel label-stride))
      (truncate-labels!
       sel channel {:bottom-padding bottom-padding :height height :tooltip tooltip})
      (rotate-ticks! sel axis)
      (ensure-title! sel axis width height)
      (when (and (:gap scale) (discrete-scale? d3-scale))
        (segment-domain! sel d3-scale)))))

(defn render! [svg {{:keys [x y]} :encode
                    :keys         [width height padding tooltip]}]
  (let [x-g   (.select svg "g.chart g.x-axis")
        y-g   (.select svg "g.chart g.y-axis")
        opts  {:width width
               :height height
               :bottom-padding (:bottom padding)
               :tooltip tooltip}]
    (render-axis! x-g x opts)
    (render-axis! y-g y opts)))

